import { FINALIZING_MEETING_STATUSES, isLiteTemplate } from "@lexicanon/shared"
import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Plural, Trans } from "@lingui/react/macro"
import { Text } from "@mantine/core"
import { useDebouncedValue } from "@mantine/hooks"
import { modals } from "@mantine/modals"
import { notifications } from "@mantine/notifications"
import { type InfiniteData, useInfiniteQuery, useQueryClient } from "@tanstack/react-query"
import { getRouteApi, useNavigate } from "@tanstack/react-router"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { MeetingAccessModal } from "../../../components/MeetingAccessModal"
import type { TMeetingCardAction } from "../../../components/MeetingCardKebab/interfaces"
import { MergeMeetingsDialog } from "../../../components/MergeMeetingsDialog"
import { MomentsSection } from "../../../components/MomentsSection"
import type { TMomentsState } from "../../../components/MomentsSection/interfaces"
import { NewMeetingDialog } from "../../../components/NewMeetingDialog"
import { useInstantStart } from "../../../components/NewMeetingDialog/useInstantStart"
import { RecoveredRecordingsBanner } from "../../../components/RecoveredRecordingsBanner"
import { RecurringSeriesCard } from "../../../components/RecurringSeriesCard"
import { ReprocessForm } from "../../../components/ReprocessForm"
import { SearchFilters } from "../../../components/SearchFilters"
import { useAuth } from "../../../hooks/useAuth"
import { useCompanyApi } from "../../../hooks/useCompanyApi"
import { useCompanyQuery } from "../../../hooks/useCompanyQuery"
import { useIsOrgAdmin } from "../../../hooks/useIsOrgAdmin"
import { useMeetingsPipelineProgress } from "../../../hooks/useMeetingsPipelineProgress"
import { useMeetingEvents } from "../../../hooks/useSSE"
import {
  deleteMeetingPermanently,
  fetchActionItemsSummary,
  fetchMeetingsPage,
  fetchMeetingsStats,
  hideMeeting,
  reprocessMeeting,
  SearchUnavailableError,
  searchMeetings,
  unhideMeeting,
} from "../../../lib/api"
import { extractErrorMessage } from "../../../lib/api/errors"
import { notifyError } from "../../../lib/notify"
import { deriveEffectiveProgress, type IMeetingCardProcessing } from "../../../lib/pipeline-progress"
import { queryKeys } from "../../../lib/query-keys"
import { useProduct } from "../../../lib/tenant"
import type { IMeetingListItem } from "../../../lib/types/meeting"
import type { TSearchChannel } from "../../../lib/types/search"
import type { ITrackedMeeting } from "../../../lib/types/streaming"
import { MeetingListView } from "../-meeting-list/MeetingListView"
import {
  DASHBOARD_LOAD_MORE_SENTINEL_ID,
  DASHBOARD_SEARCH_INPUT_ID,
  type ISection,
  type TGroupMode,
  type TListLayout,
  type TRecordingModeFilter,
} from "../-meeting-list/MeetingListView/interfaces"

/** Per-browser choice of cards vs the compact list. */
const LIST_LAYOUT_STORAGE_KEY = "lex:meeting-list-view"

function readStoredListLayout(): TListLayout {
  try {
    const stored = localStorage.getItem(LIST_LAYOUT_STORAGE_KEY)
    return stored === "list" ? "list" : "cards"
  } catch {
    return "cards"
  }
}

/** Typed handle on the dashboard route so the wrapper can read + write its
 * search params (`?q=`, filters, `?focusSearch`) without a circular import back
 * to `index.tsx`. */
const dashboardRoute = getRouteApi("/_auth/")

/** Minimum characters before the content search fires — a 1-char term is noise. */
const MIN_QUERY_LENGTH = 2
/** Warming poll cadence, and the soft deadline after which the copy softens. */
const WARMING_POLL_MS = 700
const WARMING_LONG_MS = 8000

// ---------------------------------------------------------------------------
// Date / status / people grouping
// ---------------------------------------------------------------------------

const DATE_BUCKETS = ["today", "yesterday", "week", "month", "older"] as const
type TDateBucket = (typeof DATE_BUCKETS)[number]

function dateBucket(iso: string): TDateBucket {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return "older"
  const now = new Date()
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const startOfYesterday = new Date(startOfToday.getTime() - 86_400_000)
  const startOfWeek = new Date(startOfToday.getTime() - 7 * 86_400_000)
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1)
  if (date >= startOfToday) return "today"
  if (date >= startOfYesterday) return "yesterday"
  if (date >= startOfWeek) return "week"
  if (date >= startOfMonth) return "month"
  return "older"
}

function groupMeetings(
  meetings: IMeetingListItem[],
  mode: TGroupMode,
  i18n: ReturnType<typeof useLingui>["i18n"],
): ISection[] {
  if (mode === "date") {
    const buckets = new Map<TDateBucket, IMeetingListItem[]>()
    for (const m of meetings) {
      const b = dateBucket(m.start_time)
      const arr = buckets.get(b) ?? []
      arr.push(m)
      buckets.set(b, arr)
    }
    const labels: Record<TDateBucket, string> = {
      today: i18n._(msg`Today`),
      yesterday: i18n._(msg`Yesterday`),
      week: i18n._(msg`This week`),
      month: i18n._(msg`Earlier this month`),
      older: i18n._(msg`Older`),
    }
    return DATE_BUCKETS.filter((b) => buckets.has(b)).map((b) => ({
      id: b,
      label: labels[b],
      items: buckets.get(b) ?? [],
    }))
  }

  if (mode === "status") {
    const buckets = new Map<string, IMeetingListItem[]>()
    for (const m of meetings) {
      const arr = buckets.get(m.status) ?? []
      arr.push(m)
      buckets.set(m.status, arr)
    }
    const ORDER = ["in_progress", "summarized", "transcribed", "parsed", "ended", "detected", "fetched", "error"]
    const labels: Record<string, string> = {
      in_progress: i18n._(msg`In progress`),
      summarized: i18n._(msg`Summarised`),
      transcribed: i18n._(msg`Awaiting summary`),
      parsed: i18n._(msg`Parsed`),
      ended: i18n._(msg`Ended`),
      detected: i18n._(msg`Detected`),
      fetched: i18n._(msg`Fetched`),
      error: i18n._(msg`Error`),
    }
    return ORDER.filter((s) => buckets.has(s)).map((s) => ({
      id: s,
      label: labels[s] ?? s,
      items: buckets.get(s) ?? [],
    }))
  }

  // by people — top 4 most-frequent participants
  const counts = new Map<string, number>()
  for (const m of meetings) {
    for (const p of m.speaker_names ?? []) {
      counts.set(p, (counts.get(p) ?? 0) + 1)
    }
  }
  const top = [...counts.entries()].sort((a, b) => b[1] - a[1]).slice(0, 4)
  return top.map(([name]) => ({
    id: name,
    label: name,
    items: meetings.filter((m) => (m.speaker_names ?? []).includes(name)),
  }))
}

/**
 * How recent `updated_at` must be for an `in_progress` meeting to be promoted
 * to the live hero card. The streaming pipeline touches updated_at on every
 * segment flush; a value older than this threshold means the session is dead
 * and the row will be cleaned up by the orphan reaper on the next sweep.
 */
const HERO_FRESH_THRESHOLD_MS = 5 * 60_000

/**
 * Progress floor for a finalizing card that has no persisted stage/progress yet,
 * keyed by meeting status — keeps the ghost bar off 0 until the first real SSE or
 * persisted signal lands. Mirrors the coarse stage floors in pipeline-progress.
 */
const STATUS_PROGRESS_FLOOR: Record<string, number> = {
  ended: 10,
  transcribed: 30,
  parsed: 60,
  recovering: 10,
}

/**
 * Smart wrapper for the meeting list / dashboard. Owns data fetching, the SSE
 * live-update subscription, grouping, the live-hero freshness window, the
 * card-action mutations, and the card-triggered modals. All rendering is
 * delegated to {@link MeetingListView}.
 */
export function MeetingListPage(): React.JSX.Element {
  const company = useCompanyApi()
  const product = useProduct()
  const { i18n } = useLingui()
  const navigate = useNavigate()
  const dashNavigate = dashboardRoute.useNavigate()
  const routeSearch = dashboardRoute.useSearch()
  const { user: authUser } = useAuth()
  const { start: instantStart, isStarting } = useInstantStart()
  const activeOrgId = authUser?.activeOrganizationId ?? null
  const isLite = authUser?.experienceMode === "lite"
  // `search` is the instant typing buffer that drives client-side title
  // filtering (zero-regression). Its debounced value syncs into the URL `?q=`,
  // which is the single source of truth the content-search query reads from —
  // keeping the surface shareable/bookmarkable without laggy per-keystroke nav.
  const [search, setSearch] = useState<string>(routeSearch.q ?? "")
  const [speakerInput, setSpeakerInput] = useState<string>(routeSearch.speaker ?? "")
  const [showNewDialog, setShowNewDialog] = useState<boolean>(false)
  const [showMergeDialog, setShowMergeDialog] = useState<boolean>(false)
  const [showHidden, setShowHidden] = useState<boolean>(false)
  const [group, setGroup] = useState<TGroupMode>("date")
  const [recordingModeFilter, setRecordingModeFilter] = useState<TRecordingModeFilter>("all")
  const [listLayout, setListLayout] = useState<TListLayout>(readStoredListLayout)
  const [retryAllBusy, setRetryAllBusy] = useState(false)
  const retryAllLock = useRef(false)
  // Active action triggered from a card kebab. Single instance of each modal
  // lives at this level — the kebab in each card emits an action; this state
  // decides which modal opens for which meeting.
  const [cardAction, setCardAction] = useState<{ action: TMeetingCardAction; meetingId: string } | null>(null)
  const queryClient = useQueryClient()
  const userEmail = (authUser?.email ?? "").toLowerCase()
  // Platform admin OR org owner/admin — the audience allowed to permanently
  // delete and to list hidden meetings.
  const isOrgAdmin = useIsOrgAdmin()
  // First word of the display name for the cockpit greeting; null when the
  // session carries no name (the view then drops the "…, {name}" clause).
  const firstName = (authUser?.name ?? "").trim().split(/\s+/)[0] || null

  // The client's local month boundary — passed to the stats endpoint so "this
  // month" respects the viewer's timezone. Computed once at mount (stable).
  const [monthStartIso] = useState<string>(() => {
    const now = new Date()
    return new Date(now.getFullYear(), now.getMonth(), 1).toISOString()
  })

  // Cursor-paginated infinite list. Pages arrive newest-first (start_time DESC),
  // so date grouping fills top-down as the user scrolls. The flattened `meetings`
  // array feeds every existing client behaviour (search filter, grouping, live
  // hero) unchanged — it's just a prefix of the org's meetings now, not all of them.
  // Errors is a second query (`status=error`, its own key) so that view does not
  // walk every page of the workspace.
  const isErrorFilter = recordingModeFilter === "error"
  const listQuery = useInfiniteQuery({
    queryKey: queryKeys.meetings(activeOrgId, showHidden),
    queryFn: ({ pageParam }) => {
      if (!company) throw new Error("company unavailable")
      return fetchMeetingsPage(company, { showHidden, cursor: pageParam })
    },
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: !!company,
  })
  const errorQuery = useInfiniteQuery({
    queryKey: [...queryKeys.meetings(activeOrgId, showHidden), "status", "error"] as const,
    queryFn: ({ pageParam }) => {
      if (!company) throw new Error("company unavailable")
      return fetchMeetingsPage(company, { showHidden, cursor: pageParam, status: "error" })
    },
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: !!company && isErrorFilter,
  })
  const activeQuery = isErrorFilter ? errorQuery : listQuery
  const pages = activeQuery.data
  const isLoading = activeQuery.isLoading
  const error = activeQuery.error
  const refetch = activeQuery.refetch
  const fetchNextPage = activeQuery.fetchNextPage
  const hasNextPage = activeQuery.hasNextPage
  const isFetchingNextPage = activeQuery.isFetchingNextPage

  // NOTE (audit M8, deferred): cast is NOT redundant — the Eden `MeetingListItem`
  // has `topics?: string[]` while local `IMeetingListItem` requires `topics`.
  const meetings = useMemo<IMeetingListItem[]>(
    () => (pages?.pages.flatMap((p) => p.items) ?? []) as IMeetingListItem[],
    [pages],
  )

  // Workspace totals for the cockpit — computed server-side (access-scoped) so
  // they reflect the WHOLE org, not just the loaded pages.
  const { data: stats } = useCompanyQuery({
    key: (orgId) => queryKeys.meetingStats(orgId, showHidden),
    queryFn: (client) => fetchMeetingsStats(client, { showHidden, monthStart: monthStartIso }),
  })

  const invalidateMeetings = useCallback((): void => {
    queryClient.invalidateQueries({ queryKey: queryKeys.meetings(activeOrgId, showHidden) })
    queryClient.invalidateQueries({ queryKey: queryKeys.meetingStats(activeOrgId, showHidden) })
  }, [queryClient, activeOrgId, showHidden])

  // Live per-card finalize progress, keyed by sanitized meeting id. Fed by the
  // existing company SSE stream below (onStage / onPipeline threaded into the one
  // useMeetingEvents call); the persisted seed + status floor are merged at the
  // render map. An empty org id (no active org yet) yields an empty map that
  // resets the moment a real org id arrives.
  const {
    progressById,
    onStage: onProgressStage,
    onPipeline: onProgressPipeline,
    clear: clearProgress,
  } = useMeetingsPipelineProgress(activeOrgId ?? "")

  // ---------------------------------------------------------------------------
  // Omnisearch — the same box that title-filters the list above (instant) also
  // drives a debounced deep content search. The URL holds `q` + the filters so
  // the surface is shareable; this query mirrors the `/search` page plumbing
  // (warming-retry, 503 → quiet "unavailable") and its cache key so navigating
  // between the two reuses results.
  // ---------------------------------------------------------------------------
  const [debouncedQuery] = useDebouncedValue(search, 300)
  const [debouncedSpeaker] = useDebouncedValue(speakerInput, 300)

  useEffect(() => {
    const next = debouncedQuery.trim()
    if (next === (routeSearch.q ?? "")) return
    dashNavigate({ search: (prev) => ({ ...prev, q: next.length > 0 ? next : undefined }), replace: true })
  }, [debouncedQuery, routeSearch.q, dashNavigate])

  useEffect(() => {
    const next = debouncedSpeaker.trim()
    if (next === (routeSearch.speaker ?? "")) return
    dashNavigate({ search: (prev) => ({ ...prev, speaker: next.length > 0 ? next : undefined }), replace: true })
  }, [debouncedSpeaker, routeSearch.speaker, dashNavigate])

  // One-shot: a `?focusSearch` flag (e.g. the Voxcanon header icon) moves focus
  // into the box, then clears itself so a refresh doesn't re-grab focus. We
  // reach the input by id rather than a threaded ref — the box lives in the
  // dumb view and a cross-boundary React ref trips the duplicate-@types/react
  // resolution in this workspace.
  useEffect(() => {
    if (!routeSearch.focusSearch) return
    document.getElementById(DASHBOARD_SEARCH_INPUT_ID)?.focus()
    dashNavigate({ search: (prev) => ({ ...prev, focusSearch: undefined }), replace: true })
  }, [routeSearch.focusSearch, dashNavigate])

  const contentQuery = (routeSearch.q ?? "").trim()
  const momentsEnabled = !isLite && !!company && contentQuery.length >= MIN_QUERY_LENGTH

  const searchQuery = useCompanyQuery({
    key: (orgId) =>
      queryKeys.search(
        orgId,
        contentQuery,
        routeSearch.channels,
        routeSearch.speaker,
        routeSearch.from,
        routeSearch.to,
      ),
    queryFn: (client) =>
      searchMeetings(client, {
        q: contentQuery,
        channels: routeSearch.channels,
        speaker: routeSearch.speaker,
        from: routeSearch.from,
        to: routeSearch.to,
      }),
    enabled: momentsEnabled,
    // A 503 / error shouldn't hammer the engine; "warming" is a 200 status
    // (handled by refetchInterval), not a thrown error.
    retry: false,
    refetchInterval: (query) => (query.state.data?.status === "warming" ? WARMING_POLL_MS : false),
  })

  // Soften the warming copy once it runs past the deadline; armed when status
  // first becomes "warming" and cleared when it resolves.
  const searchStatus = searchQuery.data?.status
  const [warmingLong, setWarmingLong] = useState<boolean>(false)
  useEffect(() => {
    if (searchStatus !== "warming") {
      setWarmingLong(false)
      return
    }
    const timer = setTimeout(() => setWarmingLong(true), WARMING_LONG_MS)
    return () => clearTimeout(timer)
  }, [searchStatus])

  const momentsState: TMomentsState = ((): TMomentsState => {
    if (searchQuery.error) return searchQuery.error instanceof SearchUnavailableError ? "unavailable" : "error"
    if (searchQuery.data?.status === "warming") return "warming"
    if (searchQuery.data) return "results"
    return "loading"
  })()

  const toggleChannel = useCallback(
    (channel: TSearchChannel): void => {
      dashNavigate({
        search: (prev) => {
          const current = prev.channels ?? []
          const next = current.includes(channel) ? current.filter((c) => c !== channel) : [...current, channel]
          return { ...prev, channels: next.length > 0 ? next : undefined }
        },
        replace: true,
      })
    },
    [dashNavigate],
  )

  const setFrom = useCallback(
    (value: string): void => {
      dashNavigate({ search: (prev) => ({ ...prev, from: value.length > 0 ? value : undefined }), replace: true })
    },
    [dashNavigate],
  )

  const setTo = useCallback(
    (value: string): void => {
      dashNavigate({ search: (prev) => ({ ...prev, to: value.length > 0 ? value : undefined }), replace: true })
    },
    [dashNavigate],
  )

  const clearFilters = useCallback((): void => {
    setSpeakerInput("")
    // Keep the query (and any pending focus flag); drop only the refinements.
    dashNavigate({ search: (prev) => ({ q: prev.q, focusSearch: prev.focusSearch }), replace: true })
  }, [dashNavigate])

  const activeFilterCount =
    (routeSearch.channels?.length ?? 0) +
    (speakerInput.trim().length > 0 ? 1 : 0) +
    (routeSearch.from ? 1 : 0) +
    (routeSearch.to ? 1 : 0)

  // No `useCallback` — React Compiler memoizes; manual deps tripped the
  // `react-hooks/preserve-manual-memoization` lint rule because the
  // compiler's inferred dep set was narrower than ours.
  const handleCardAction = (meetingId: string, action: TMeetingCardAction): void => {
    if (action === "transcript") {
      navigate({ to: "/meetings/$id/transcript", params: { id: meetingId } })
      return
    }
    if (action === "reprocess" || action === "access") {
      setCardAction({ action, meetingId })
      return
    }
    if (action === "toggle-hidden") {
      if (!company) return
      const target = meetings.find((m) => m.id === meetingId)
      const willHide = !(target?.hidden ?? false)
      const fn = willHide ? hideMeeting : unhideMeeting
      fn(company, meetingId)
        .then(() => {
          invalidateMeetings()
          notifications.show({
            color: "success",
            message: willHide ? i18n._(msg`Meeting hidden`) : i18n._(msg`Meeting unhidden`),
          })
        })
        .catch((err) => {
          notifyError(err, i18n._(msg`Could not update visibility`))
        })
      return
    }
    if (action === "delete") {
      if (!company) return
      // Two audiences, one menu item. Org owners/admins (and platform admins)
      // erase the meeting for good; a plain member who merely organized it can
      // only soft-delete (hide) — which is exactly what the API allows, so the
      // affordance can't lead them into a 403.
      modals.openConfirmModal({
        title: isOrgAdmin ? i18n._(msg`Delete meeting permanently?`) : i18n._(msg`Delete this meeting?`),
        children: (
          <Text size="sm">
            {isOrgAdmin ? (
              <Trans>This action cannot be undone. The recording, transcript, and analysis are removed.</Trans>
            ) : (
              <Trans>It is removed from everyone's list. An org admin can restore it or delete it for good.</Trans>
            )}
          </Text>
        ),
        labels: { confirm: i18n._(msg`Delete`), cancel: i18n._(msg`Cancel`) },
        confirmProps: { color: "danger" },
        onConfirm: () => {
          const run = isOrgAdmin ? deleteMeetingPermanently(company, meetingId) : hideMeeting(company, meetingId)
          run
            .then(() => {
              invalidateMeetings()
              notifications.show({ color: "success", message: i18n._(msg`Meeting deleted`) })
            })
            .catch((err) => {
              notifyError(err, i18n._(msg`Delete failed`))
            })
        },
      })
    }
  }

  // Drives the "{n} open actions" count in the page subline.
  const { data: actionsSummary } = useCompanyQuery({
    key: queryKeys.actionItemsSummary,
    queryFn: (client) => fetchActionItemsSummary(client),
    enabled: product === "lexicanon" && !isLite,
  })

  const connected = useMeetingEvents(
    useCallback(
      (tracked: ITrackedMeeting) => {
        // Patch the infinite-query cache in place: update an existing row's status
        // wherever it sits across loaded pages, else prepend a stub to the first
        // page (newest-first ordering keeps a just-started meeting at the top).
        const safeId = tracked.meetingId.replace(/[^a-zA-Z0-9_-]/g, "_")
        // De-ghost the card the moment it reaches a terminal phase — the live
        // progress entry is meaningless once the summary (or an error) is in.
        if (tracked.phase === "summarized" || tracked.phase === "error") clearProgress(safeId)
        const key = queryKeys.meetings(activeOrgId, showHidden)
        const cached =
          queryClient.getQueryData<InfiniteData<{ items: IMeetingListItem[]; nextCursor: string | null }>>(key)
        const existing = cached?.pages.flatMap((pg) => pg.items).find((m) => m.id === safeId)
        queryClient.setQueryData<InfiniteData<{ items: IMeetingListItem[]; nextCursor: string | null }>>(
          key,
          (prev) => {
            if (!prev) return prev
            if (existing) {
              return {
                ...prev,
                pages: prev.pages.map((pg) => ({
                  ...pg,
                  items: pg.items.map((m) => (m.id === safeId ? { ...m, status: tracked.phase } : m)),
                })),
              }
            }
            if (prev.pages.length === 0) return prev
            const [first, ...rest] = prev.pages
            const stub: IMeetingListItem = {
              id: safeId,
              subject: tracked.subject,
              organizer_name: tracked.organizer_name,
              organizer_email: tracked.organizer_email,
              start_time: tracked.start_time,
              status: tracked.phase,
              updated_at: new Date().toISOString(),
            }
            return { ...prev, pages: [{ ...first, items: [stub, ...first.items] }, ...rest] }
          },
        )
        // Completion refetch: the in-place patch above only carries `status`,
        // but at "summarized" the server has fields that didn't exist before —
        // the pipeline-inferred subject, topics, counts. Invalidate so the row
        // shows its real title without a manual reload.
        if (existing && existing.status !== "summarized" && tracked.phase === "summarized") {
          invalidateMeetings()
        }
      },
      [queryClient, activeOrgId, showHidden, invalidateMeetings, clearProgress],
    ),
    onProgressStage,
    onProgressPipeline,
  )

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim()
    const list = q
      ? meetings.filter(
          (m) =>
            m.subject.toLowerCase().includes(q) ||
            (m.organizer_name ?? "").toLowerCase().includes(q) ||
            (m.topics ?? []).some((t) => t.toLowerCase().includes(q)) ||
            (m.speaker_names ?? []).some((s) => s.toLowerCase().includes(q)),
        )
      : meetings
    const byMode =
      recordingModeFilter === "all"
        ? list
        : recordingModeFilter === "error"
          ? list.filter((m) => m.status === "error")
          : list.filter((m) => {
              const lite = isLiteTemplate(m.template_id)
              return recordingModeFilter === "lite" ? lite : !lite
            })
    return [...byMode].sort((a, b) => new Date(b.start_time).getTime() - new Date(a.start_time).getTime())
  }, [meetings, search, recordingModeFilter])

  const handleListLayoutChange = (next: TListLayout): void => {
    setListLayout(next)
    try {
      localStorage.setItem(LIST_LAYOUT_STORAGE_KEY, next)
    } catch {
      // Private mode or blocked storage. The choice still holds this session.
    }
  }

  // Bulk actions cover the failed meetings on the pages loaded so far.
  // The Errors query pages on its own; it does not drain the whole workspace.
  const failedMeetings = filtered.filter((m) => m.status === "error")

  const handleRetryAllFailed = (): void => {
    if (!company || retryAllLock.current || failedMeetings.length === 0) return
    const api = company
    const targets = failedMeetings
    const total = targets.length
    retryAllLock.current = true
    setRetryAllBusy(true)
    const toastId = "retry-all-failed"
    notifications.show({
      id: toastId,
      loading: true,
      autoClose: false,
      withCloseButton: false,
      message: i18n._(msg`Trying again… 0 of ${total}`),
    })
    void (async (): Promise<void> => {
      let done = 0
      let failed = 0
      for (const meeting of targets) {
        try {
          await reprocessMeeting(api, meeting.id)
        } catch {
          failed += 1
        }
        done += 1
        notifications.update({
          id: toastId,
          loading: true,
          autoClose: false,
          withCloseButton: false,
          message: i18n._(msg`Trying again… ${done} of ${total}`),
        })
      }
      notifications.update({
        id: toastId,
        loading: false,
        autoClose: 4000,
        color: failed > 0 ? "danger" : "success",
        message:
          failed > 0
            ? i18n._(msg`Couldn't try again for ${failed} of ${total} meetings.`)
            : i18n._(msg`Finished trying again.`),
      })
      invalidateMeetings()
      retryAllLock.current = false
      setRetryAllBusy(false)
    })()
  }

  const handleDeleteAllFailed = (): void => {
    if (!company || retryAllLock.current || failedMeetings.length === 0) return
    const api = company
    const targets = failedMeetings
    const count = targets.length
    modals.openConfirmModal({
      title: isOrgAdmin ? i18n._(msg`Delete these meetings permanently?`) : i18n._(msg`Delete these meetings?`),
      children: (
        <Text size="sm">
          {isOrgAdmin ? (
            <Plural
              value={count}
              one="Delete # failed meeting for good. The recording is removed."
              other="Delete # failed meetings for good. The recordings are removed."
            />
          ) : (
            <Plural
              value={count}
              one="Remove # failed meeting from everyone's list."
              other="Remove # failed meetings from everyone's list."
            />
          )}
        </Text>
      ),
      labels: { confirm: i18n._(msg`Delete`), cancel: i18n._(msg`Cancel`) },
      confirmProps: { color: "danger" },
      onConfirm: () => {
        if (retryAllLock.current) return
        retryAllLock.current = true
        setRetryAllBusy(true)
        void (async (): Promise<void> => {
          let failed = 0
          for (const meeting of targets) {
            try {
              if (isOrgAdmin) await deleteMeetingPermanently(api, meeting.id)
              else await hideMeeting(api, meeting.id)
            } catch {
              failed += 1
            }
          }
          invalidateMeetings()
          notifications.show({
            color: failed > 0 ? "danger" : "success",
            message:
              failed > 0
                ? i18n._(msg`Deleted ${count - failed} of ${count}. ${failed} could not be deleted.`)
                : isOrgAdmin
                  ? i18n._(msg`Deleted ${count} meetings`)
                  : i18n._(msg`Removed ${count} meetings`),
          })
          retryAllLock.current = false
          setRetryAllBusy(false)
        })()
      },
    })
  }

  // Hero card only when there's a *fresh* in_progress meeting; stale rows fall
  // into the regular grid where they'll show with their normal status pill.
  // `nowMs` is state-tracked (refreshed every minute) so React Compiler can
  // treat it as a deterministic input — calling Date.now() directly during
  // render is impure and rejected.
  const [nowMs, setNowMs] = useState<number>(() => Date.now())
  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 60_000)
    return () => clearInterval(id)
  }, [])
  const live = useMemo(
    () =>
      filtered.find((m) => {
        if (m.status !== "in_progress") return false
        if (!m.updated_at) return false
        const age = nowMs - new Date(m.updated_at).getTime()
        return Number.isFinite(age) && age >= 0 && age <= HERO_FRESH_THRESHOLD_MS
      }) ?? null,
    [filtered, nowMs],
  )
  const nonLive = useMemo(() => filtered.filter((m) => m.id !== live?.id), [filtered, live])
  const sections = useMemo(() => groupMeetings(nonLive, group, i18n), [nonLive, group, i18n])

  // Per-card finalize progress for the ghost variant. A finalizing meeting shows
  // the live SSE-derived entry when one exists (never regressing below its
  // persisted baseline), else a seed derived from its persisted stage/progress,
  // else a coarse status floor so a just-ended card isn't stuck at 0. Meetings
  // outside the finalizing set get no entry — that's what de-ghosts a card the
  // instant its status patches to `summarized`.
  const processingById = useMemo<ReadonlyMap<string, IMeetingCardProcessing>>(() => {
    const out = new Map<string, IMeetingCardProcessing>()
    for (const m of meetings) {
      if (!FINALIZING_MEETING_STATUSES.has(m.status)) continue
      const persistedProgress = m.processing_progress ?? null
      const liveEntry = progressById.get(m.id)
      if (liveEntry) {
        out.set(m.id, { ...liveEntry, progress: Math.max(liveEntry.progress, persistedProgress ?? 0) })
        continue
      }
      const persistedStage = m.processing_stage ?? null
      if (persistedStage === null && persistedProgress === null) {
        out.set(m.id, { stage: null, progress: STATUS_PROGRESS_FLOOR[m.status] ?? 0, currentStepName: null })
        continue
      }
      out.set(
        m.id,
        deriveEffectiveProgress({
          pipeline: null,
          sseStage: null,
          sseProgress: null,
          persistedStage,
          persistedProgress,
        }),
      )
    }
    return out
  }, [meetings, progressById])

  // Workspace-wide cockpit totals now come from the server (access-scoped) so
  // they cover the WHOLE org, not just the loaded pages. Fall back to the loaded
  // set while the stats query is still in flight, so the numbers never flash 0
  // when meetings are already on screen.
  const totalCount = stats?.total ?? meetings.length
  const totalMinutes = stats?.totalMinutes ?? 0
  const monthCount = stats?.monthCount ?? 0

  // Infinite-scroll: auto-load the next page as the sentinel nears the viewport,
  // plus an explicit "Load more" button. The sentinel is observed by id (same
  // ref-across-boundary avoidance as the search box). Re-runs when the loaded set
  // grows or the search mode toggles (the sentinel mounts/unmounts).
  const loadMore = useCallback((): void => {
    if (hasNextPage && !isFetchingNextPage) void fetchNextPage()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  useEffect(() => {
    // The sentinel only exists in the grouped view with more pages to load; the
    // guard makes hasNextPage + momentsEnabled real deps so the observer
    // (re)attaches exactly when the sentinel mounts/unmounts.
    if (!hasNextPage || momentsEnabled) return
    const el = document.getElementById(DASHBOARD_LOAD_MORE_SENTINEL_ID)
    if (!el) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) loadMore()
      },
      // The list is not virtualised. A 400px margin left a blank gap on a fast
      // scroll: the next page had not started loading when the sentinel
      // arrived. 1600px starts that fetch while the current page is still
      // on screen.
      { rootMargin: "1600px" },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [loadMore, hasNextPage, momentsEnabled])

  const momentsSection = momentsEnabled ? (
    <MomentsSection
      query={contentQuery}
      state={momentsState}
      hits={searchQuery.data?.hits ?? []}
      engine={searchQuery.data?.engine ?? null}
      warmingLong={warmingLong}
      errorMessage={searchQuery.error?.message}
      onRetry={() => {
        void searchQuery.refetch()
      }}
    />
  ) : null

  const filtersContent = (
    <SearchFilters
      channels={routeSearch.channels ?? []}
      onToggleChannel={toggleChannel}
      speaker={speakerInput}
      onSpeakerChange={setSpeakerInput}
      from={routeSearch.from ?? ""}
      to={routeSearch.to ?? ""}
      onFromChange={setFrom}
      onToChange={setTo}
      onClearAll={clearFilters}
      hasActiveFilters={activeFilterCount > 0}
    />
  )

  return (
    <>
      <RecoveredRecordingsBanner />
      <MeetingListView
        product={product}
        variant={isLite ? "lite" : "full"}
        isStarting={isStarting}
        totalCount={totalCount}
        firstName={firstName}
        monthCount={monthCount}
        totalMinutes={totalMinutes}
        filteredIsEmpty={filtered.length === 0}
        sections={sections}
        filteredMeetings={nonLive}
        processingById={processingById}
        live={live}
        connected={connected}
        isLoading={isLoading}
        // Pass the unwrapped raw detail (Eden envelopes → readable string); the
        // view shows a friendly headline and tucks this behind a "Details"
        // disclosure rather than surfacing it as the primary message.
        error={error ? extractErrorMessage(error) : null}
        search={search}
        onSearchChange={setSearch}
        momentsActive={isLite ? false : momentsEnabled}
        momentsSection={isLite ? undefined : momentsSection}
        filtersContent={isLite ? undefined : filtersContent}
        activeFilterCount={activeFilterCount}
        group={group}
        onGroupChange={setGroup}
        recordingModeFilter={recordingModeFilter}
        onRecordingModeFilterChange={setRecordingModeFilter}
        listLayout={listLayout}
        onListLayoutChange={handleListLayoutChange}
        onRetryAllFailed={handleRetryAllFailed}
        onDeleteAllFailed={handleDeleteAllFailed}
        retryAllBusy={retryAllBusy}
        showHidden={showHidden}
        onShowHiddenChange={setShowHidden}
        isOrgAdmin={isOrgAdmin}
        userEmail={userEmail}
        actionsCount={actionsSummary?.count ?? 0}
        onCardAction={handleCardAction}
        onNewMeeting={isLite ? instantStart : () => setShowNewDialog(true)}
        onMerge={() => setShowMergeDialog(true)}
        onOpenLive={(id) => navigate({ to: "/meetings/$id", params: { id } })}
        onRetry={() => refetch()}
        recurringSeriesCard={isLite ? undefined : product === "lexicanon" ? <RecurringSeriesCard /> : null}
        hasMore={hasNextPage}
        isLoadingMore={isFetchingNextPage}
        onLoadMore={loadMore}
      />

      {!isLite && <NewMeetingDialog open={showNewDialog} onClose={() => setShowNewDialog(false)} />}

      <MergeMeetingsDialog
        opened={showMergeDialog}
        onClose={() => setShowMergeDialog(false)}
        meetings={meetings
          .filter((m) => m.status === "summarized" || m.status === "transcribed")
          .map((m) => ({
            id: m.id,
            subject: m.subject,
            start_time: m.start_time,
            status: m.status,
            template_id: m.template_id ?? null,
          }))}
        onMerged={(newId) => {
          navigate({ to: "/meetings/$id", params: { id: newId } })
        }}
      />

      {/* Single instance of each card-triggered modal, controlled by `cardAction`. */}
      {cardAction?.action === "access" && (
        <MeetingAccessModal
          opened
          onClose={() => setCardAction(null)}
          meetingId={cardAction.meetingId}
          onChanged={invalidateMeetings}
        />
      )}
      {cardAction?.action === "reprocess" && (
        <ReprocessForm
          meetingId={cardAction.meetingId}
          opened
          onClose={() => setCardAction(null)}
          onReprocessed={() => {
            invalidateMeetings()
            setCardAction(null)
          }}
        />
      )}
    </>
  )
}
